/*
 * Feedback on AI output (crud/llm_feedback.py), everywhere someone uses an
 * LLM in HQ: generated profiles, write-up drafts, CV imports and the local
 * page assistant. Reviewers (`llm_feedback`) see everything on the LLM
 * Feedback page, with profile refinement requests as implicit feedback.
 */
export type LlmRating = 'up' | 'down';
export type LlmFeature = 'profile_generation' | 'project_writeup' | 'cv_import' | 'page_assistant';

export const LLM_FEATURE_LABELS: Record<LlmFeature, string> = {
  profile_generation: 'Profile generation',
  project_writeup: 'Write-up draft',
  cv_import: 'CV import',
  page_assistant: 'Page assistant',
};

// Mirrors crud/llm_feedback.FEATURE_TAGS (the backend drops tags not listed for the feature).
export const LLM_FEEDBACK_FEATURE_TAGS: Record<LlmFeature, string[]> = {
  profile_generation: ['inaccurate', 'made_up_detail', 'missing_information', 'too_long', 'too_short', 'wrong_tone',
    'marketing_language', 'formatting', 'ignored_refinement', 'great'],
  project_writeup: ['inaccurate', 'made_up_detail', 'missing_information', 'not_specific', 'missed_skills',
    'included_negatives', 'too_long', 'too_short', 'wrong_tone', 'great'],
  cv_import: ['missed_skills', 'wrong_skills', 'wrong_levels', 'missed_jobs', 'wrong_job_details', 'made_up_detail', 'great'],
  page_assistant: ['wrong_answer', 'made_up_detail', 'missed_page_data', 'too_long', 'too_slow', 'great'],
};
export type LlmFeedbackStatus = 'new' | 'reviewed' | 'actioned';

export const LLM_FEEDBACK_TAG_LABELS: Record<string, string> = {
  inaccurate: 'Inaccurate',
  made_up_detail: 'Made-up detail',
  missing_information: 'Missing information',
  too_long: 'Too long',
  too_short: 'Too short',
  wrong_tone: 'Wrong tone',
  marketing_language: 'Marketing language',
  formatting: 'Formatting',
  ignored_refinement: 'Ignored my refinement',
  not_specific: 'Not specific enough',
  missed_skills: 'Missed skills',
  included_negatives: 'Included negatives',
  wrong_skills: 'Wrong skills',
  wrong_levels: 'Wrong levels',
  missed_jobs: 'Missed jobs',
  wrong_job_details: 'Wrong job details',
  wrong_answer: 'Wrong answer',
  missed_page_data: 'Missed what was on the page',
  too_slow: 'Too slow',
  great: 'Great',
};

export interface LlmGenerationDetails {
  id: string;
  status?: string;
  output?: string;
  warnings?: string[];
  unsupported_claims?: string[];
  repaired?: boolean;
  draft_scores?: number[];
  revision_retried?: boolean;
  model_id?: string;
  model_used?: string;
  prompt_version_id?: string;
  client_request?: string;
  feedback?: string;
  base_generation_id?: string;
  requested_by?: string;
  requested_at?: number;
  started_at?: number;
  finished_at?: number;
  worker_ms?: number;
  model_latency_ms?: number;
  input_tokens?: number;
  output_tokens?: number;
  call_count?: number;
  failed_call_count?: number;
  word_count?: number;
  inserted_at?: number;
  sent_at?: number;
  edit_similarity?: number;
  error?: string;
  estimated_cost_usd?: number | null;
}

export interface LlmFeedbackEntry {
  id: string;
  kind: 'feedback' | 'refinement';
  feature: string;
  target_id: string;
  rating?: LlmRating;
  tags?: string[];
  comment?: string;
  submitted_by_email?: string;
  created_at?: string;
  updated_at?: string;
  profile_id?: string;
  person_id?: string;
  person_name?: string | null;
  profile_label?: string | null;
  model_used?: string;
  prompt_version_id?: string;
  was_revision?: boolean;
  status?: LlmFeedbackStatus | 'n/a';
  reviewer_note?: string;
  reviewed_by_email?: string;
  reviewed_at?: string;
  generation?: LlmGenerationDetails | null;
  base_generation?: LlmGenerationDetails | null;
  project_id?: string;
  // Snapshot of what was rated, for features without a stored generation.
  context?: {
    mode?: string;
    project_name?: string;
    output?: string;
    skill_count?: number;
    review_count?: number;
    file_name?: string;
    status?: string;
    job_count?: number;
    skills?: { name?: string; proficiency?: number; confidence?: string }[];
    jobs?: { role?: string; employer?: string; client?: string; start_date?: string; end_date?: string; current?: boolean }[];
    skills_error?: string;
    experience_error?: string;
    // cv_import: counts only (uploader-only CVs); skills / jobs / file_name are legacy.
    has_summary?: boolean;
    summary_error?: string;
    question?: string;
    route?: string;
    // Checkpoint write-up drafts: links only, never anything from the reviews.
    reviews?: { review_id: string; date?: string }[];
    by_reviewee?: boolean; // checkpoint drafts: the draft is kept only when true
  };
}

export interface LlmFeedbackTally { up: number; down: number; total: number }

export interface LlmFeedbackReport {
  entries: LlmFeedbackEntry[];
  summary: {
    feedback: number;
    refinements: number;
    up: number;
    down: number;
    open: number;
    by_prompt_version: Record<string, LlmFeedbackTally>;
    by_model: Record<string, LlmFeedbackTally>;
    by_feature?: Record<string, LlmFeedbackTally>;
    tags: Record<string, number>;
  };
  tags: string[];
}
